import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import Address from "_shared/addresses/address";
import EmailAddress from "_shared/email-addresses/email-address";
import Guid from "_shared/guids/guid";
import PhoneNumber from "_shared/phone-numbers/phone-number";
import InsuranceInfo from "marketplace/insurance-info/insurance-info";
import RegistrationNumber from "marketplace/registration-number/registration-number";
import TechnicalBackgroundInfo from "marketplace/technical-background-info/technical-background-info";
import IndividualProfileSchema, { IndividualProfileJSON, IndividualProfileParams } from "marketplace/individuals/profiles/individual-profile-schema";
import BillingRateInfo from "marketplace/teams/fee-schedules/rates/billing-rate-info";

export default class IndividualProfile {
  public static readonly EXAMPLE: IndividualProfile = IndividualProfile.create({
    userId: Guid.generate(),
    legalEntityId: Guid.generate(),
    entityName: "Test Entity",
    firstName: "John",
    lastName: "Doe",
    credentials: "Experienced legal professional.",
    location: Address.EXAMPLE,
    phoneNumber: new PhoneNumber("+1234567890"),
    avatarId: Guid.generate(),
    email: EmailAddress.create("john.doe@example.com"),
    school: "Harvard Law School",
    degree: "Juris Doctor",
    resumeId: Guid.generate(),
    professionalRegistration: new RegistrationNumber("REG123456"),
    technicalBackground: new TechnicalBackgroundInfo("Software Development"),
    numberOfApplicationsDraftedProsecuted: 50,
    numberOfAllowedApplications: 100,
    barAdmissionYear: 2010,
    isOnCall: true,
    insured: InsuranceInfo.EXAMPLE,
    billingRate: BillingRateInfo.EXAMPLE,
    potentialDiversityCandidate: true,
    collaborates: true,
    additionalInfo: "Available for remote work.",
    categories: ["Corporate Law", "Intellectual Property"],
    links: ["https://linkedin.com/in/johndoe"],
    professionalPublications: ["https://example.com/publication1", "https://example.com/publication2"],
    sampleApplications: ["https://example.com/sample-application1", "https://example.com/sample-application2"],
    fluentLanguages: ["English", "Spanish"],
    technicalLanguages: ["Python", "JavaScript"],
    administrativeRegions: ["California", "New York"],
    regionsRegistered: ["California"],
    regionsLicensed: ["California", "New York"]
  });

  public static create(values: IndividualProfileParams): IndividualProfile {
    const parsed = IndividualProfileSchema.validate(values);
    if (isNullOrUndefined(parsed)) {
      throw new Error("Invalid IndividualProfile parameters.");
    }
    return new IndividualProfile(parsed);
  }

  public static fromJSON(json: IndividualProfileJSON): IndividualProfile {
    if (isNullOrUndefined(json)) return json;
    const params = IndividualProfileSchema.deserialize(json);
    return new IndividualProfile(params);
  }

  public readonly userId?: Guid;
  public readonly legalEntityId?: Guid;
  public readonly entityName?: string;
  public readonly firstName: string;
  public readonly lastName: string;
  public readonly credentials?: string;
  public readonly location?: Address;
  public readonly phoneNumber?: PhoneNumber;
  public readonly avatarId?: Guid;
  public readonly email?: EmailAddress;
  public readonly school?: string;
  public readonly degree?: string;
  public readonly resumeId?: Guid;
  public readonly professionalRegistration?: RegistrationNumber;
  public readonly technicalBackground?: TechnicalBackgroundInfo;
  public readonly numberOfApplicationsDraftedProsecuted?: number;
  public readonly numberOfAllowedApplications?: number;
  public readonly barAdmissionYear?: number;
  public readonly isOnCall?: boolean = false;
  public readonly insured?: InsuranceInfo;
  public readonly billingRate?: BillingRateInfo;
  public readonly potentialDiversityCandidate?: boolean = false;
  public readonly collaborates?: boolean = false;
  public readonly additionalInfo?: string;
  public readonly categories?: string[] = [];
  public readonly links?: string[] = [];
  public readonly professionalPublications?: string[] = [];
  public readonly sampleApplications?: string[] = [];
  public readonly fluentLanguages?: string[] = [];
  public readonly technicalLanguages?: string[] = [];
  public readonly administrativeRegions?: string[] = [];
  public readonly regionsRegistered?: string[] = [];
  public readonly regionsLicensed?: string[] = [];

  public constructor(params: IndividualProfileParams) {
    this.userId = params.userId;
    this.legalEntityId = params.legalEntityId;
    this.entityName = params.entityName;
    this.firstName = params.firstName;
    this.lastName = params.lastName;
    this.credentials = params.credentials;
    this.location = params.location ? Address.create(params.location) : undefined;
    this.phoneNumber = params.phoneNumber;
    this.avatarId = params.avatarId;
    this.email = params.email;
    this.school = params.school;
    this.degree = params.degree;
    this.resumeId = params.resumeId;
    this.professionalRegistration = params.professionalRegistration;
    this.technicalBackground = params.technicalBackground;
    this.numberOfApplicationsDraftedProsecuted = params.numberOfApplicationsDraftedProsecuted;
    this.numberOfAllowedApplications = params.numberOfAllowedApplications;
    this.barAdmissionYear = params.barAdmissionYear;
    this.isOnCall = params.isOnCall ?? false;
    this.insured = params.insured ? InsuranceInfo.create(params.insured) : undefined;
    this.billingRate = params.billingRate ? BillingRateInfo.create(params.billingRate) : undefined;
    this.potentialDiversityCandidate = params.potentialDiversityCandidate ?? false;
    this.collaborates = params.collaborates ?? false;
    this.additionalInfo = params.additionalInfo;
    this.categories = params.categories ?? [];
    this.links = params.links ?? [];
    this.professionalPublications = params.professionalPublications ?? [];
    this.sampleApplications = params.sampleApplications ?? [];
    this.fluentLanguages = params.fluentLanguages ?? [];
    this.technicalLanguages = params.technicalLanguages ?? [];
    this.administrativeRegions = params.administrativeRegions ?? [];
    this.regionsRegistered = params.regionsRegistered ?? [];
    this.regionsLicensed = params.regionsLicensed ?? [];
  }

  public get fullName(): string { return `${this.firstName} ${this.lastName}` }

  public toJSON(): IndividualProfileJSON {
    return IndividualProfileSchema.serialize(this);
  }

  public isEqualTo(other: IndividualProfile | null | undefined): boolean {
    if (!other) return false;
    return this.userId?.isEqualTo(other.userId) ?? false;
  }

  public toString(): string {
    return `IndividualProfile (
      userId: ${this.userId?.toString()}
      legalEntityId: ${this.legalEntityId?.toString()}
      entityName: ${this.entityName}
      firstName: ${this.firstName}
      lastName: ${this.lastName}
      description: ${this.credentials}
      location: ${this.location?.toString()}
      phoneNumber: ${this.phoneNumber?.toString()}
      email: ${this.email?.toString()}
      school: ${this.school}
      degree: ${this.degree}
      barAdmissionYear: ${this.barAdmissionYear}
      isOnCall: ${this.isOnCall}
      insured: ${this.insured?.toString()}
      billingRate: ${this.billingRate?.toString()}
      potentialDiversityCandidate: ${this.potentialDiversityCandidate}
      collaborates: ${this.collaborates}
      additionalInfo: ${this.additionalInfo}
      categories: ${this.categories}
      links: ${this.links}
      professionalPublications: ${this.professionalPublications}
      sampleApplications: ${this.sampleApplications}
      fluentLanguages: ${this.fluentLanguages}
      technicalLanguages: ${this.technicalLanguages}
      administrativeRegions: ${this.administrativeRegions}
      regionsRegistered: ${this.regionsRegistered}
      regionsLicensed: ${this.regionsLicensed}
    )`;
  }
}
